import { useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';
import roomStyles from '../Room.module.css';
import styles from './ProjectsRoom.module.css';

const projects = [
  {
    title: 'Fakturirane.bg',
    channel: '01',
    year: '2012 / 2026',
    stack: [
      'PHP',
      'Symfony Components',
      'CycleORM',
      'MySQL',
      'Vue.js',
      'TypeScript',
      'Naive UI',
      'HTML',
      'CSS',
    ],
    description: 'Business invoicing platform for daily accounting workflows.',
    url: 'https://fakturirane.bg/',
    accent: '#f3d84b',
  },
  {
    title: 'Komandirovam.bg',
    channel: '02',
    year: '2026',
    stack: ['Next.js', 'TypeScript', 'PrimeReact', 'Prisma', 'HTML', 'CSS'],
    description: 'Business travel orders and expenses in one clear workflow.',
    url: 'https://komandirovam.bg/',
    accent: '#62d9d0',
  },
  {
    title: 'Unior.bg',
    channel: '03',
    year: '2018',
    stack: ['WordPress', 'HTML', 'CSS'],
    description: 'Product-led web presence for a professional tool brand.',
    url: 'https://unior.bg/',
    accent: '#ff705d',
  },
  {
    title: 'Convertors',
    channel: '04',
    year: '2024',
    stack: ['Laravel', 'HTML', 'CSS'],
    description: 'Fast tools for practical file and business-data conversion.',
    url: 'https://converter.bulmaroffice.com/',
    accent: '#9ce56b',
  },
] as const;

type RemoteKey = 'power' | 'prev' | 'next';
type RemotePress = { key: RemoteKey; phase: 'half' | 'pressed' } | null;
type ProjectsRoomProps = { sectionRef: RefObject<HTMLElement | null>; showIdleHint: boolean };

const remoteButtons: Array<{ key: RemoteKey; label: string; className: string }> = [
  { key: 'power', label: 'Turn project television on or off', className: styles.remotePower },
  { key: 'prev', label: 'Show previous project', className: styles.remotePrev },
  { key: 'next', label: 'Show next project', className: styles.remoteNext },
];

export function ProjectsRoom({ sectionRef, showIdleHint }: ProjectsRoomProps) {
  const remoteTimersRef = useRef<number[]>([]);
  const [projectIndex, setProjectIndex] = useState(0);
  const [tvOn, setTvOn] = useState(true);
  const [remotePress, setRemotePress] = useState<RemotePress>(null);
  const [channelRevision, setChannelRevision] = useState(0);
  const selectedProject = projectIndex < projects.length ? projects[projectIndex] : null;

  useEffect(() => () => remoteTimersRef.current.forEach(window.clearTimeout), []);

  const activateRemote = (key: RemoteKey) => {
    remoteTimersRef.current.forEach(window.clearTimeout);
    remoteTimersRef.current = [];
    const applyAction = () => {
      if (key === 'power') {
        setTvOn((current) => !current);
        setChannelRevision((current) => current + 1);
        return;
      }
      if (!tvOn) return;
      setProjectIndex((current) =>
        key === 'prev' ? Math.max(0, current - 1) : Math.min(projects.length, current + 1),
      );
      setChannelRevision((current) => current + 1);
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      applyAction();
      return;
    }
    setRemotePress({ key, phase: 'half' });
    remoteTimersRef.current.push(
      window.setTimeout(() => {
        setRemotePress({ key, phase: 'pressed' });
        applyAction();
      }, 90),
    );
    remoteTimersRef.current.push(
      window.setTimeout(() => setRemotePress({ key, phase: 'half' }), 245),
    );
    remoteTimersRef.current.push(window.setTimeout(() => setRemotePress(null), 380));
  };

  return (
    <section
      ref={sectionRef}
      className={`${roomStyles.room} ${roomStyles.wideRoom} ${styles.room}`}
      aria-label="Interactive pixel-art project television room"
    >
      <div className={`${roomStyles.scene} ${styles.scene}`}>
        <picture className={roomStyles.background}>
          <source media="(max-width: 680px)" srcSet="/assets/v5-room-projects-mobile.png" />
          <source media="(min-width: 681px)" srcSet="/assets/v5-room-projects-ultrawide.png" />
          <img
            src="/assets/v5-room-projects.png"
            alt="Cozy pixel-art living room with a large CRT television and a coffee table"
          />
        </picture>
        <div
          className={`${styles.projectScreen} ${tvOn ? styles.projectScreenOn : styles.projectScreenOff}`}
          aria-live="polite"
        >
          {!tvOn ? (
            <div className={styles.offAir}>
              <span>•</span>
              <b>OFF AIR</b>
              <small>PRESS POWER</small>
            </div>
          ) : selectedProject ? (
            <article
              key={`${projectIndex}-${channelRevision}`}
              className={styles.teletextPanel}
              style={{ '--project-accent': selectedProject.accent } as CSSProperties}
            >
              <header>
                <b>PROJECT TV</b>
                <span>CH {selectedProject.channel}/04</span>
              </header>
              <h2>{selectedProject.title}</h2>
              <div className={styles.projectMeta}>
                <span>YEAR</span>
                <b>{selectedProject.year}</b>
                <span>STATUS</span>
                <b>ONLINE</b>
              </div>
              <h3>TECHNOLOGIES</h3>
              <p className={styles.projectStack}>{selectedProject.stack.join(' · ')}</p>
              <h3>ABOUT</h3>
              <p className={styles.projectDescription}>{selectedProject.description}</p>
              <a href={selectedProject.url} target="_blank" rel="noreferrer">
                OPEN PROJECT ↗
              </a>
            </article>
          ) : (
            <div key={`signal-${channelRevision}`} className={styles.noSignal}>
              <i aria-hidden="true" />
              <div>
                <span>CH 05</span>
                <b>NO SIGNAL</b>
                <strong>
                  YOUR PROJECT
                  <br />
                  COULD BE HERE
                </strong>
                <small>← RETURN TO PROJECTS</small>
              </div>
            </div>
          )}
          {showIdleHint && (
            <output className={styles.idlePrompt}>
              <span>CONTINUE EXPLORING</span>
              <b>
                SCROLL ↓ OR
                <br />
                USE THE REMOTE
              </b>
            </output>
          )}
        </div>
        <div className={styles.remote} aria-label="Project television remote control">
          {remoteButtons.map(({ key, label, className }) => {
            const phase = remotePress?.key === key ? remotePress.phase : null;
            return (
              <button
                key={key}
                type="button"
                className={`${styles.remoteButton} ${className} ${phase === 'half' ? styles.remoteButtonHalf : ''} ${phase === 'pressed' ? styles.remoteButtonPressed : ''}`}
                onClick={() => activateRemote(key)}
                aria-label={label}
              />
            );
          })}
        </div>
        <p className={styles.remoteHint}>POWER · PREV · NEXT</p>
      </div>
    </section>
  );
}
